import type { PluginListenerHandle } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';
import { useCallback, useState } from 'react';

import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import {
  isProductHash,
  ndefMessageToValue,
  parseScannedProductValue,
} from '@/core/utils/product-link.utils';

export type ProductScanResult = { hash: string; productType?: string };

export type UseProductScanReturn = {
  /** Native only — opens the system barcode scanner (fast Apple Vision / ML Kit). */
  startQrScan: (() => Promise<ProductScanResult | null>) | null;
  /** Only available on native; scans NFC tag and returns hash/productType from NDEF URL or text. */
  startNfcScan: (() => Promise<ProductScanResult | null>) | null;
  isQrScanning: boolean;
  isNfcScanning: boolean;
  error: string | null;
};

export type UseProductScanOptions = {
  emptyCodeMessage?: string;
  invalidCodeMessage?: string;
  sessionTimeoutMessage?: string;
  sessionFailedMessage?: string;
  scanInstructions?: string;
  cameraPermissionMessage?: string;
  qrScanFailedMessage?: string;
  nfcAlertMessage?: string;
};

type NfcScanMessages = {
  emptyCodeMessage: string;
  invalidCodeMessage: string;
  sessionTimeoutMessage: string;
  sessionFailedMessage: string;
  alertMessage: string;
};

/**
 * Hook for native product scanning (QR via system camera, NFC via Core NFC).
 * Web QR scanning stays in ProductScanModal (html5-qrcode).
 */
export function useProductScan(
  options: UseProductScanOptions = {},
): UseProductScanReturn {
  const [isQrScanning, setIsQrScanning] = useState(false);
  const [isNfcScanning, setIsNfcScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startQrScanFn =
    useCallback(async (): Promise<ProductScanResult | null> => {
      setError(null);
      setIsQrScanning(true);
      try {
        const { scanProductQrNative } =
          await import('@/core/utils/scan-product-qr-native');
        return await scanProductQrNative(
          {
            emptyCodeMessage:
              options.emptyCodeMessage ?? 'No product code found in QR code',
            invalidCodeMessage:
              options.invalidCodeMessage ??
              'This does not look like a valid up2promo product code',
            cameraPermissionMessage:
              options.cameraPermissionMessage ??
              'Camera access was denied. Allow camera access in Settings and try again.',
            scanFailedMessage:
              options.qrScanFailedMessage ?? 'QR scan failed. Try again.',
          },
          options.scanInstructions,
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'QR scan failed';
        setError(message);
        return null;
      } finally {
        setIsQrScanning(false);
      }
    }, [
      options.emptyCodeMessage,
      options.invalidCodeMessage,
      options.cameraPermissionMessage,
      options.qrScanFailedMessage,
      options.scanInstructions,
    ]);

  const startNfcScanFn =
    useCallback(async (): Promise<ProductScanResult | null> => {
      setError(null);
      setIsNfcScanning(true);
      try {
        return await startNfcScanNative({
          emptyCodeMessage:
            options.emptyCodeMessage ?? 'No product code found on NFC tag',
          invalidCodeMessage:
            options.invalidCodeMessage ??
            'This does not look like a valid up2promo product code',
          sessionTimeoutMessage:
            options.sessionTimeoutMessage ?? 'No NFC tag detected. Try again.',
          sessionFailedMessage:
            options.sessionFailedMessage ?? 'The NFC session failed.',
          alertMessage:
            options.nfcAlertMessage ?? 'Hold your device near the NFC tag.',
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'NFC scan failed';
        setError(message);
        return null;
      } finally {
        setIsNfcScanning(false);
      }
    }, [
      options.emptyCodeMessage,
      options.invalidCodeMessage,
      options.sessionTimeoutMessage,
      options.sessionFailedMessage,
      options.nfcAlertMessage,
    ]);

  const native = isCapacitorNative();

  return {
    startQrScan: native ? startQrScanFn : null,
    startNfcScan: native ? startNfcScanFn : null,
    isQrScanning,
    isNfcScanning,
    error,
  };
}

async function startNfcScanNative(
  messages: NfcScanMessages,
): Promise<ProductScanResult | null> {
  const { CapacitorNfc } = await import('@capgo/capacitor-nfc');

  const { supported } = await CapacitorNfc.isSupported();
  if (!supported) {
    throw new Error('NFC is not supported on this device');
  }

  const listeners: PluginListenerHandle[] = [];

  const cleanup = async () => {
    await Promise.allSettled(listeners.map((listener) => listener.remove()));
    listeners.length = 0;
    await CapacitorNfc.stopScanning().catch(() => {});
  };

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = async (outcome: ProductScanResult | null | Error) => {
      if (settled) return;
      settled = true;
      await cleanup();
      if (outcome instanceof Error) {
        reject(outcome);
      } else {
        resolve(outcome);
      }
    };

    void (async () => {
      try {
        listeners.push(
          await CapacitorNfc.addListener('nfcEvent', (event) => {
            const value = ndefMessageToValue(event.tag?.ndefMessage ?? null);
            if (!value.trim()) {
              void finish(new Error(messages.emptyCodeMessage));
              return;
            }

            const parsed = parseScannedProductValue(value);
            if (!parsed.hash.trim()) {
              void finish(new Error(messages.emptyCodeMessage));
              return;
            }
            if (!isProductHash(parsed.hash)) {
              void finish(new Error(messages.invalidCodeMessage));
              return;
            }
            void finish(parsed);
          }),
        );

        listeners.push(
          await CapacitorNfc.addListener('nfcSessionEnd', ({ reason }) => {
            if (reason === 'userCancelled') {
              void finish(null);
              return;
            }
            if (reason === 'sessionTimeout') {
              void finish(new Error(messages.sessionTimeoutMessage));
              return;
            }
            void finish(new Error(messages.sessionFailedMessage));
          }),
        );

        await CapacitorNfc.startScanning({
          invalidateAfterFirstRead: true,
          alertMessage: messages.alertMessage,
          ...(Capacitor.getPlatform() === 'ios'
            ? { iosSessionType: 'tag' as const }
            : {}),
        });
      } catch (err) {
        void finish(err instanceof Error ? err : new Error('NFC scan failed'));
      }
    })();
  });
}
